import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import settingsService from '../../services/settingsService';
import { Check, X, Bell, BellOff } from 'lucide-react';

const NotificationsSection = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [notifications, setNotifications] = useState({
    daily_recap_enabled: true,
    focus_reminders_enabled: true,
    weekly_digest_enabled: true
  });

  useEffect(() => {
    loadSettings();
  }, [user]);

  const loadSettings = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const settings = await settingsService.getUserSettings(user.id);
      setNotifications({
        daily_recap_enabled: settings.daily_recap_enabled ?? true,
        focus_reminders_enabled: settings.focus_reminders_enabled ?? true,
        weekly_digest_enabled: settings.weekly_digest_enabled ?? true
      });
    } catch (error) {
      console.error('Error loading notification settings:', error);
      setMessage({ type: 'error', text: 'Failed to load notification settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (key, value) => {
    const updatedNotifications = {
      ...notifications,
      [key]: value
    };
    
    setNotifications(updatedNotifications);

    try {
      await settingsService.updateUserSettings(user.id, { [key]: value });
      setMessage({ type: 'success', text: 'Notification settings updated' });
      setTimeout(() => setMessage({ type: '', text: '' }), 2000);
    } catch (error) {
      console.error('Error updating notification settings:', error);
      setMessage({ type: 'error', text: 'Failed to update settings' });
      // Revert on error
      setNotifications(notifications);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  const notificationItems = [
    {
      key: 'daily_recap_enabled',
      title: 'Daily Recap',
      description: 'Get a daily summary of your activity and insights',
      icon: '📧'
    },
    {
      key: 'focus_reminders_enabled',
      title: 'Focus Reminders',
      description: 'Receive reminders about your active focuses',
      icon: '🎯'
    },
    {
      key: 'weekly_digest_enabled',
      title: 'Weekly Digest',
      description: 'Get a weekly overview of your progress and patterns',
      icon: '📊'
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Notifications</h2>
        <p className="text-gray-600 mt-1">
          Manage how Swirl keeps you informed
        </p>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`flex items-center space-x-2 p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          {message.type === 'success' ? <Check size={20} /> : <X size={20} />}
          <span>{message.text}</span>
        </div>
      )}

      {/* Notification Toggles */}
      <div className="space-y-4">
        {notificationItems.map(item => {
          const isEnabled = notifications[item.key];
          
          return (
            <div
              key={item.key}
              className="flex items-start justify-between p-4 border border-gray-200 rounded-lg hover:border-purple-300 transition-colors"
            >
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center text-2xl flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{item.title}</h3>
                  <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                </div>
              </div>
              
              <button
                onClick={() => handleToggle(item.key, !isEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  isEnabled ? 'bg-purple-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          );
        })}
      </div>

      {/* Email Preferences Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Bell size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-blue-900 mb-2">
              Email Notifications
            </h4>
            <p className="text-sm text-blue-800 mb-2">
              Notifications will be sent to: <span className="font-medium">{user?.email}</span>
            </p>
            <p className="text-sm text-blue-800">
              You can unsubscribe from any notification type using the link at the bottom of each email.
            </p>
          </div>
        </div>
      </div>

      {/* Push Notifications (Future) */}
      <div className="pt-6 border-t border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Push Notifications
        </h3>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <BellOff size={20} className="text-gray-500" />
            <div>
              <p className="text-sm text-gray-700 font-medium">
                Push notifications coming soon
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Get instant notifications on your device for important updates
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationsSection;





